<div class="heading skin-detail-heading" (resize)="handleResize($event)">
  @if (canGoBack()) {
    <app-button2 appearance="secondary-ghost" size="s" ariaLabel="Go back" (click)="handleGoBack()">
      <app-icon name="chevron-left" />
    </app-button2>
  }

  @if (titleStatusPrefix()) {
    <!-- [Pending], [Canceled]…: the title's own 16px medium, never larger (Johanna, 2026-10-22; it was 20px). -->
    <span class="shrink-0 text-[16px] font-medium leading-7 text-gray-950">{{ titleStatusPrefix() }}</span>
  }

  <app-input
    class="input"
    appearance="sapphire-inline"
    [inputFormControl]="form.controls.title"
    placeholder="New Event"
    [errorPlaceholder]="'Add title'"
    [withErrors]="submitted"
    size="s"
  />

  <div class="icons">
    <div [appPreventDirtyClick]="dirtyFormRef" class="contents">
      <!-- [MUST-002] 'Bug' debug quick-action hidden per task; uncomment to re-enable
      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="bug"
        [active]="selectedTab === 'debug'"
        matTooltip="Debug"
        (onClick)="selectedTab = 'debug'"
      />
      -->

      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-archive2"
        [active]="form.controls.archived.value"
        [matTooltip]="form.controls.archived.value ? Labels.RestoreFromArchive : Labels.Archive"
        [disabled]="!event?.id"
        (onClick)="archive()"
      />

      <stch-trash-action
        *ngIf="!contracted && !isRemoteEvent"
        [deleted]="form.controls.deleted.value"
        [disabled]="!event?.id"
        (onClick)="delete()"
      />

      <ng-container *ngComponentOutlet="workspaceComponents['app-item-workspace-chip']; inputs: workspaceChipInputs" />
      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-share"
        matTooltip="Share"
        [disabled]="!event?.id"
        [stchShareContextMenu]="event"
        [stchShareContextMenuDisabled]="!event?.id"
      />

      <app-button2
        appearance="secondary-ghost"
        size="xs"
        ariaLabel="More options"
        matTooltip="More options"
        [disabled]="!event?.id"
        (appEventContextMenuTab)="$event === 'access' ? openWorkspaceAccess() : openItemComments()"
        [appEventContextMenuElementLocation]="UILocations.mainView"
        [appEventContextMenu]="event"
        [appEventContextMenuDisabled]="!event?.id"
        appEventContextMenuMode="popover"
        [printEnabled]="true"
      >
        <app-icon name="sapphire-ellipsis" />
      </app-button2>
    </div>

    @if (showCloseButton) {
      <app-button appearance="sapphire-secondary-ghost" size="xs" icon="sapphire-close" (click)="handleClose()" />
    }
  </div>
</div>

<!-- The line under the title says when the event happens and how it repeats, with its calendar's badge on the right
     (Johanna, 2026-10-21). Created · Updated moved under the description in Details, as in Tasks.
     Only once the event is saved: New Event sets the time in its Starts and Ends fields, and the empty line
     would only push the tabs down (Johanna, 2026-10-21). -->
<!-- Johanna, 2026-10-22: the line wraps rather than cutting the timing off; it starts under the title, past the
     side panel's back arrow (16px + its 20px + the 4px gap); Mail's To line size; the badge sits level with the
     line, centred on it when it wraps. -->
<!-- The badge drops under the line when the panel is too narrow for both (it broke the line letter by letter). -->
<div class="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 pb-1 pr-8">
  @if (event?.id) {
    <!-- 4px under the title, 22px lines so wrapped lines breathe (Johanna, 2026-10-22). -->
    <p
      class="m-0 mt-2 min-w-0 flex-[1_1_160px] break-words text-base leading-[22px] text-gray-600"
      [class.pl-8]="!canGoBack()"
      [class.pl-20]="canGoBack()"
    >
      @for (line of headerWhenLines(); track $index) {
        <span class="block">{{ line }}</span>
      }
    </p>
  } @else {
    <span class="flex-1" aria-hidden="true"></span>
  }
  <ng-container *ngComponentOutlet="workspaceComponents['app-item-team-label']; inputs: workspaceLabelInputs" />
  <!-- stitch-app-shell FolderBadge: the event's calendar in its colour. Only once
       the event is saved: in New Event the Calendar field is where it's chosen (Johanna, 2026-10-21). -->
  @if (event?.id && headerCalendar(); as calendar) {
    <app-container-badge
      type="calendar"
      class="min-w-0 shrink-0"
      [title]="calendar.title"
      [color]="calendar.color"
    />
  } @else if (locationBadge(); as badge) {
    <app-search-location-badge class="min-w-0 shrink-0" [type]="badge.type" [name]="badge.name ?? ''" [color]="badge.color" />
  }
</div>

@if (!event?.id && workspaceIdentity(); as identity) {
  <app-tenancy-selector
    [spaceTitle]="identity.spaceTitle"
    [spaceTenantId]="identity.spaceTenantId"
    [memberTenantId]="identity.memberTenantId"
    itemNoun="event"
    [locked]="saveInProgress || !!event?.accountId || form.controls.uploads.value.length > 0"
    [lockedReason]="
      saveInProgress
        ? 'Wait for this event to finish saving.'
        : event?.accountId
          ? 'This draft already belongs to a workspace.'
          : null
    "
    [tenantId]="sharingTenantId()"
    (tenantIdChange)="setSharingTenantId($event)"
  />
}

<div class="tabs skin-detail-tabs">
  <app-tabs
    #workspaceTabControl
    appearance="sapphire"
    [values]="workspaceTabs"
    [value]="selectedTab"
    (valueChange)="selectWorkspaceTab($event)"
    [overflow]="tabsOverflow()"
    size="m"
  />

  <div class="filler"></div>

  @if (handoverTenantId) {
    <app-button2
      appearance="secondary"
      size="m"
      [height]="32"
      rounding="lg"
      data-handover-anchor
      [ariaLabel]="handoverControl.accessibleName()"
      [ariaHaspopup]="'dialog'"
      [disabled]="!handoverCanAssign || handoverControl.busy()"
      [attr.title]="handoverCanAssign ? null : 'Requires edit access'"
      [ariaDescribedBy]="
        !handoverCanAssign
          ? handoverControl.ids.title + '-edit'
          : handoverControl.busy()
            ? handoverControl.ids.busy
            : null
      "
      (click)="handoverCanAssign && handoverControl.openAssign()"
    >
      <app-icon name="sapphire-user-plus" size="m" />
      {{ handoverControl.buttonLabel() }}
      <app-icon name="sapphire-chevron-down" size="s" />
      <span class="sr-only" [id]="handoverControl.ids.title + '-edit'">{{
        handoverCanAssign ? '' : 'Requires edit access'
      }}</span>
    </app-button2>
  }
  <stch-knowledge-button [active]="displayKnowledge" (click)="showKnowledge()" />
</div>
<app-item-handover
  #handoverControl
  [api]="handoverApi"
  [enabled]="!!handoverTenantId"
  [showButton]="false"
  [itemId]="handoverItemId"
  [tenantId]="handoverTenantId"
  [insideModal]="insideModal()"
  [matchItemSignals]="true"
  [stitchType]="StitchType.EVENT"
  [prototypeWindow]="true"
  noun="event"
/>
<app-item-review
  [api]="reviewApi"
  [enabled]="!!handoverTenantId"
  [itemId]="handoverItemId"
  [tenantId]="handoverTenantId"
  [insideModal]="insideModal()"
  [stitchType]="StitchType.EVENT"
  [contentVersion]="reviewContentVersion()"
/>

@if (isRemoteEvent) {
  <stch-alert
    class="mx-8 mt-8"
    type="info"
    [customConfig]="{ title: 'Event received via invitation' }"
    message="You can edit your copy. Your changes stay on your calendar and are not sent to the organizer; the organizer's next update replaces them. Time, repeat rule and participants are set by the organizer."
  />
}

<div
  class="form"
  appDroppable
  appDroppableHoverClass="drag-over"
  [appDroppableDisabled]="!stitchItem?.id"
  [appDroppablePredicate]="dndPredicate"
  (appDroppableDrop)="dndDrop($event)"
>
  <div class="drop-area-border"></div>

  @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
    <app-entity-hub-search [contactId]="event?.id" />
  }

  <div class="form-heading">
    <div class="form-heading-nav">
      <div class="form-heading-nav-separator"></div>

    </div>

    <div *ngIf="displayKnowledge" class="form-heading-nav-underline"></div>

    <!-- One Knowledge box, rows in the skin's order: Knots · Tags · Connections; on the Stitch tab, main's
         knowledge hub mode (#2481). -->
    <div class="form-knowledge" [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())">
      <app-knots
        class="form-knots"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="event | toArray"
        [control]="form.controls['knots']"
        [stateKey]="StateKey.knotsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="hubEntityUid"
      />

      <app-tags
        class="form-tags"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="[event]"
        [control]="form.controls['tags']"
        [stateKey]="StateKey.tagsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="hubEntityUid"
      />

      <app-connections
        class="form-knots"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="[event]"
        [control]="form.controls['connections']"
        [stateKey]="StateKey.connectionsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="hubEntityUid"
      />
    </div>

    <div class="form-heading-nav-underline"></div>
  </div>

  <app-scroll
    *ngIf="(selectedTab !== 'stitch' || (permissionsLoaded() && !hasKnowledgeHubAccess()))"
    [ngSwitch]="selectedTab"
    class="form-scroll"
    (top)="displayScrollShadow = !$event"
  >
    <div *ngIf="displayScrollShadow" class="form-scroll-shadow"></div>

    <!--
      stitch-app-shell FormKit (EventForm): when (Starts · Ends · Repeats, All day and the time zone), Reminders, then
      who and where (Participants, Calendar · Location, Meeting link) and the description; every field named above it.
    -->
    <div *ngSwitchCase="'event'" class="form-kit event-details" [class.contracted]="contracted">
      <!-- RSVP is only answerable while the invitation is live (SBUX-729 C-7/C-8 — the buttons used to
           stay clickable on a dead event and mailed the organizer a reply for it). A cancellation is
           worth explaining: the organizer caused it and the user cannot undo it. Trash and archive are
           the user's own reversible actions, so the section simply disappears — restoring the event
           brings it back. -->
      @if (offersRsvp && (rsvpLifecycle === 'live' || rsvpLifecycle === 'cancelled')) {
        <section class="form-kit-section">
          @if (rsvpLifecycle === 'live') {
            <div class="details-rsvp">
              <div class="details-rsvp-label">Are you attending?</div>
              <app-event-rsvp-buttons
                [event]="rsvpEvent()"
                [scopeResolver]="rsvpScopeResolver"
                (replied)="onRsvpReplied($event)"
              />
            </div>
          } @else {
            <stch-alert
              data-testid="rsvp-cancelled-alert"
              type="error"
              message="This event has been cancelled"
              [customConfig]="{ title: '' }"
              class="block"
            />
          }
        </section>
      }

      <section class="form-kit-section">
        <div class="form-kit-row">
          <app-date-range-picker
            class="form-kit-field-range"
            startLabel="Starts"
            endLabel="Ends"
            separator="none"
            [fromDate]="form.controls.startDate"
            [toDate]="form.controls.endDate"
            [fromTime]="form.controls.startTime"
            [disabled]="isRemoteEvent"
            [toTime]="form.controls.endTime"
            [allDay]="form.controls.allDay.value"
            [autoAdjustEnd]="true"
            [datesRequired]="true"
          />

          <div class="form-kit-field">
            <span class="form-kit-label">Repeats</span>
            <app-recurrence-picker
              appearance="sapphire-outline"
              [control]="form.controls.recurrence"
              [referenceDate]="form.controls.startDate"
              [allDay]="form.controls.allDay"
              [timeZone]="form.controls.timeZone"
              size="m"
              [disabled]="isRemoteEvent"
            />
          </div>
        </div>

        <div class="form-kit-inline">
          <app-checkbox2 [formControl]="form.controls.allDay" label="All day" size="m" />

          <app-timezone-input
            [control]="form.controls.timeZone"
            placeholder="Select Time Zone"
            appearance="sapphire"
            buttonSize="m"
            leadingIcon="sapphire-globe"
            [disabled]="isRemoteEvent"
            [caption]="isRemoteEvent ? null : tzSeeder.caption()"
          />
        </div>
      </section>

      <section class="form-kit-section form-kit-section-compact">
        <h3 class="form-kit-section-title">Reminders</h3>
        <stch-reminders-picker [control]="form.controls.reminders" />
      </section>

      <section class="form-kit-section">
        <div class="form-kit-field">
          <span class="form-kit-label">Participants</span>
          <app-participants-input
            [tenantItems]="event?.accountId ? [event] : undefined"
            [requestContext]="requestContext"
            [control]="form.controls.participants"
            [readonlyMode]="event.isRemoteEvent"
          />
        </div>

        <div class="form-kit-row">
          <div class="form-kit-field">
            <span class="form-kit-label">Calendar</span>
            <app-autocomplete-input
              appearance="sapphire-outline"
              placeholder="Add To Calendar"
              [suggestions]="calendarSuggestions()"
              [inputFormControl]="form.controls.calendarId"
              size="s"
              [withErrors]="true"
              [validationMessages]="{ required: 'Calendar is required' }"
              [withClear]="true"
            />
          </div>

          <div class="form-kit-field">
            <span class="form-kit-label">Location</span>
            <app-input
              appearance="sapphire-outline"
              [inputFormControl]="form.controls.location.controls.address"
              placeholder="Add a place"
              [withErrors]="submitted"
              [withClear]="true"
              size="s"
            />
          </div>
        </div>

      @if (needsNewCalendar && newCalendarStatus() !== 'ready') {
        <p role="status">
          @switch (newCalendarStatus()) {
            @case ('empty') {
              This workspace has no calendar available for new events. Create a calendar before saving this event.
            }
            @case ('error') {
              Calendars could not be loaded. Choose another workspace or reopen this event.
            }
            @default {
              Loading calendars for this workspace.
            }
          }
        </p>
      }

        <div class="form-kit-field">
          <span class="form-kit-label">Meeting link</span>
          <stch-meeting-link-field [control]="form.controls.meetingLink" />
        </div>

        <stch-item-description-field
          [control]="form.controls.description"
          [item]="event"
          [tagsControl]="form.controls['tags']"
          [knotsControl]="form.controls['knots']"
        />

        @if (event?.createdAt) {
          <app-stitch-timestamps class="form-kit-timestamps mt-4 block" [createdAt]="event?.createdAt" [updatedAt]="event?.updatedAt" />
        }
      </section>

      <!-- Team items (item handover): who is responsible and who made the event, as a FormKit InfoList. -->
      @if (handoverTenantId) {
        <section class="form-kit-section">
          <dl class="form-kit-info">
            <div class="form-kit-info-row">
              <dt>Responsible</dt>
              <dd data-testid="details-responsible">{{ handoverControl.responsibleName() }}</dd>
            </div>
            @if (handoverControl.creatorName(); as author) {
              <div class="form-kit-info-row">
                <dt>Created by</dt>
                <dd data-testid="details-authored-by">{{ author }}</dd>
              </div>
            }
          </dl>
        </section>
      }
    </div>

    <app-attachments-list
      class="form-attachments"
      *ngSwitchCase="'attachments'"
      [stitchItem]="stitchItem"
      [inputControl]="form.controls.uploads"
      [tenantItems]="tenantCarrier()"
      [contextMenuStateVariant]="attachmentsMenuStateVariant"
      (listStateChange)="attachmentsListState = $event"
      (onClickItem)="handleClickItem($event)"
      (onDblClickItem)="handleDblClickItem($event)"
      (uploading)="onUploadingChange($event)"
    />

    <app-context-menu-state-indicator
      *ngSwitchCase="'stitch'"
      class="block ml-auto w-fit mt-5 mr-4"
      svgIcon="filters"
      appStitchListContextMenu
      (appStitchListContextMenuChange)="stitchListState = $event"
    />

    <app-linked-info-list
      *ngSwitchCase="'stitch'"
      class="form-linked-info"
      [stitch]="event"
      [state]="stitchListState"
      (clickStitchItem)="handleClickItem($event)"
      (dblClickStitchItem)="handleDblClickItem($event)"
    />

    <div *ngSwitchCase="'access'" class="form-linked-info">
      <ng-container
        *ngComponentOutlet="workspaceComponents['app-item-workspace-access']; inputs: workspaceAccessInputs"
      />
    </div>

    <stch-comments
      (countChanged)="commentsCount.set($event)"
      [insideTeam]="insideTeam"
      *ngSwitchCase="'comments'"
      class="form-linked-info"
      [stitch]="event"
    />

    <ng-container *ngSwitchCase="'activity'">
      @if (!footerActivityPresentation()) {
        @if (itemChangesInputs; as inputs) {
          <ng-container *ngComponentOutlet="workspaceComponents['app-item-changes']; inputs: inputs" />
        }
      }
    </ng-container>

    <stch-json-viewer *ngSwitchCase="'debug'" [json]="event?.debug" />
  </app-scroll>

  @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
    <app-stitch-hub
      [stitch]="event"
      (clickStitchItem)="handleClickItem($event)"
      (dblClickStitchItem)="handleDblClickItem($event)"
    />
  }

  <stch-save-cancel-panel
    [enabled]="!pristine"
    [uploading]="isUploading() || (needsNewCalendar && newCalendarStatus() !== 'ready')"
    (onCancel)="handleCancel()"
    (onSave)="handleSubmit()"
  />

</div>

<ng-container *ngComponentOutlet="workspaceComponents['app-item-quick-comment']; inputs: quickCommentInputs" />

<!-- stitch-app-shell breadcrumbs footer in the main view, as in every app; elsewhere the workspace footer. -->
@if (navigation.enabled && event?.id) {
  <app-detail-breadcrumbs
    [crumbs]="breadcrumbs()"
    [status]="form.controls.archived.value ? 'Archived' : form.controls.deleted.value ? 'Trash' : undefined"
  />
} @else {
  <ng-container *ngComponentOutlet="workspaceComponents['app-item-workspace-footer']; inputs: workspaceFooterInputs" />
}
